import React from 'react';
import { Text, View } from '@react-pdf/renderer';
import { styles } from './PdfStyles';
import { formatDate, formatDateRange, formatTime, isAllDay } from '../../../../utils/dateUtils';

/**
 * PDF-Header mit Titel und Firmendaten.
 */
export const PdfHeader = ({ companyName, userName, exportDate }) => (
    <View style={styles.header}>
        <Text style={styles.title}>Kolonnenführer – Arbeitsbericht</Text>
        <Text style={styles.subtitle}>
            {companyName || 'Optimox Manager'} · Erstellt von {userName || '—'} · {exportDate}
        </Text>
    </View>
);

/**
 * Meta-Zusammenfassung des Exports.
 */
export const PdfMeta = ({ dateRange, dateMode, assignmentCount, exportStyle }) => {
    const getRangeText = () => {
        if (dateMode === 'undated') return 'Undatiert';
        
        const rangeStr = dateRange?.start ? formatDateRange(dateRange.start, dateRange.end) : 'Gesamtzeitraum';
        
        if (dateMode === 'combined') {
            return `${rangeStr} & Undatiert`;
        }
        return rangeStr;
    };

    return (
        <View style={styles.meta}>
            <View style={styles.metaItem}>
                <Text style={styles.metaLabel}>Zeitraum</Text>
                <Text style={styles.metaValue}>{getRangeText()}</Text>
            </View>
            <View style={styles.metaItem}>
                <Text style={styles.metaLabel}>Anzahl Einsätze</Text>
                <Text style={styles.metaValue}>{assignmentCount}</Text>
            </View>
            <View style={styles.metaItem}>
                <Text style={styles.metaLabel}>Berichtstyp</Text>
                <Text style={styles.metaValue}>{exportStyle === 'compact' ? 'Kompakt' : 'Detailliert'}</Text>
            </View>
        </View>
    );
};

/**
 * Strukturierte Übersicht der Kolonnen und deren Besetzung.
 */
/**
 * Strukturierte Übersicht der Kolonnen und deren Besetzung.
 */
export const PdfResourceOverview = ({ kolonnen = [], workers = [], vehicles = [], options = {} }) => {
    const showKolonnen = options.showKolonnenList;
    const showWorkers = options.showWorkerList;
    const showVehicles = options.showVehicleList;
    
    // Wenn gar nichts gewählt ist, nichts rendern
    if (!showKolonnen && !showWorkers && !showVehicles) return null;

    // Nur Kolonnen zählen, die auch Mitglieder haben
    const activeKolonnen = showKolonnen ? kolonnen.filter(k => {
        const hasWorkers = workers.some(w => (k.members || []).some(m => m.type === 'worker' && m.resource_id === w.id));
        const hasVehicles = vehicles.some(v => (k.members || []).some(m => m.type === 'vehicle' && m.resource_id === v.id));
        return hasWorkers || hasVehicles;
    }) : [];

    return (
        <View>
            {showKolonnen && activeKolonnen.length > 0 && (
                <>
                    <Text style={styles.sectionTitle}>Kolonnenliste ({activeKolonnen.length})</Text>
                    {activeKolonnen.map((k, i) => {
                        const kWorkers = workers.filter(w => (k.members || []).some(m => m.type === 'worker' && m.resource_id === w.id));
                        const kVehicles = vehicles.filter(v => (k.members || []).some(m => m.type === 'vehicle' && m.resource_id === v.id));
                        
                        return (
                            <View key={i} style={styles.resourceGroup} wrap={false}>
                                <Text style={styles.resourceGroupTitle}>Kolonne: {k.internal_name || k.name}</Text>
                                {kWorkers.map(w => (
                                    <Text key={w.id} style={styles.resourceItem}>• {w.name} ({w.role || 'Mitarbeiter'})</Text>
                                ))}
                                {kVehicles.map(v => (
                                    <Text key={v.id} style={styles.resourceItem}>• {v.license_plate} ({v.category_name || 'Fahrzeug'})</Text>
                                ))}
                            </View>
                        );
                    })}
                </>
            )}

            {showWorkers && workers.length > 0 && (
                <PdfDataTable 
                    title="Mitarbeiterliste"
                    headers={['Name', 'Rolle', 'Qualifikation']}
                    data={workers.map(w => ({ 
                        name: w.name, 
                        role: w.role || 'Mitarbeiter', 
                        qual: w.qualification || '—' 
                    }))}
                    keys={['name', 'role', 'qual']}
                    colWidths={['40%', '30%', '30%']}
                />
            )}

            {showVehicles && vehicles.length > 0 && (
                <PdfDataTable 
                    title="Fahrzeugliste"
                    headers={['Kennzeichen', 'Bezeichnung', 'Kategorie']}
                    data={vehicles.map(v => ({ 
                        plate: v.license_plate, 
                        name: v.internal_name || '—', 
                        cat: v.category_name || 'Fahrzeug' 
                    }))}
                    keys={['plate', 'name', 'cat']}
                    colWidths={['30%', '40%', '30%']}
                />
            )}
        </View>
    );
};

/**
 * Table für Personal oder Fahrzeuge.
 */
export const PdfDataTable = ({ title, headers, data, keys, colWidths }) => (
    <View>
        <Text style={styles.sectionTitle}>{title} ({data.length})</Text>
        <View style={styles.table}>
            <View style={styles.tableHeader}>
                {headers.map((h, i) => (
                    <Text key={i} style={[styles.tableHeaderCell, { width: colWidths[i] }]}>{h}</Text>
                ))}
            </View>
            {data.map((row, i) => (
                <View key={i} style={[styles.tableRow, i % 2 === 1 ? { backgroundColor: '#fcfdfe' } : {}]} wrap={false}>
                    {keys.map((key, ki) => (
                        <Text key={ki} style={[styles.tableCell, { width: colWidths[ki] }, ki === 0 ? { fontFamily: 'Helvetica-Bold' } : {}]}>
                            {row[key] || '—'}
                        </Text>
                    ))}
                </View>
            ))}
        </View>
    </View>
);

/**
 * KOMPAKT-VERSION: Effiziente Darstellung aller Einsatzdetails.
 * Integriert alle Informationen in einem dichten, aber lesbaren Block.
 */
/**
 * Hilfsfunktion zum Gruppieren von Ressourcen eines Einsatzes.
 */
const groupResources = (assignment, kolonnen = []) => {
    const groups = {}; // Format: { [id]: { name, workers, vehicles } }
    const individuals = { workers: [], vehicles: [] };

    (assignment.workers || []).forEach(w => {
        if (w.kolonne_id) {
            if (!groups[w.kolonne_id]) {
                const kMaster = kolonnen.find(k => String(k.id) === String(w.kolonne_id));
                groups[w.kolonne_id] = { 
                    name: kMaster?.name || w.kolonne_name || 'Unbekannte Kolonne', 
                    workers: [], 
                    vehicles: [] 
                };
            }
            groups[w.kolonne_id].workers.push(w);
        } else {
            individuals.workers.push(w);
        }
    });

    (assignment.vehicles || []).forEach(v => {
        if (v.kolonne_id) {
            if (!groups[v.kolonne_id]) {
                const kMaster = kolonnen.find(k => String(k.id) === String(v.kolonne_id));
                groups[v.kolonne_id] = { 
                    name: kMaster?.name || v.kolonne_name || 'Unbekannte Kolonne', 
                    workers: [], 
                    vehicles: [] 
                };
            }
            groups[v.kolonne_id].vehicles.push(v);
        } else {
            individuals.vehicles.push(v);
        }
    });

    return { groups, individuals };
};

/**
 * KOMPAKT-VERSION: Effiziente Darstellung aller Einsatzdetails.
 * Integriert alle Informationen in einem dichten, aber lesbaren Block.
 */
export const PdfAssignmentCardShort = ({ assignment, options, companyLoc, kolonnen = [] }) => {
    const statusLabel = {
        aktiv: 'Aktiv',
        pausiert: 'Pausiert',
        abgeschlossen: 'Erledigt'
    };

    const { groups, individuals } = groupResources(assignment, kolonnen);

    // Hilfsfunktion für robuste Adress-Findung
    const getStartAddr = () => {
        const aAddr = assignment.start_address?.trim();
        if (aAddr) return aAddr;
        const cAddr = companyLoc?.address?.trim();
        if (cAddr) return cAddr;
        return null;
    };

    // Fahrtzeiten-Formatierung
    const formatDuration = (min) => min ? `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m` : null;
    const ttTo = formatDuration(assignment.travel_time_to);
    const ttReturn = formatDuration(assignment.travel_time_return);
    const workDur = formatDuration(assignment.work_duration);

    const startAddr = getStartAddr();

    return (
        <View style={styles.assignmentCardShort} wrap={false}>
            <View style={styles.assignmentHeaderShort}>
                <Text style={styles.assignmentTitleShort}>{assignment.project_name}</Text>
                <Text style={styles.assignmentMetaShort}>
                    {assignment.start_datetime ? (
                        <>
                            {formatDate(assignment.start_datetime)} · {isAllDay(assignment) ? 'Ganztag' : `${formatTime(assignment.start_datetime)} - ${formatTime(assignment.end_datetime)}`}
                        </>
                    ) : (
                        "Undatiert"
                    )} · {statusLabel[assignment.status] || assignment.status}
                </Text>
            </View>

            <View style={{ marginBottom: 4 }}>
                <View style={{ flexDirection: 'row', gap: 10 }}>
                    <View style={{ flex: 1 }}>
                        <Text style={styles.labelSmallBold}>START:</Text>
                        <Text style={styles.assignmentLocationShort}>{startAddr || '(Standard-Ort)'}</Text>
                    </View>
                    <View style={{ flex: 1 }}>
                        <Text style={styles.labelSmallBold}>ZIEL:</Text>
                        <Text style={styles.assignmentLocationShort}>{assignment.location || '(Einsatzort)'}</Text>
                    </View>
                </View>
            </View>

            <View style={styles.compactSubRow}>
                <View style={[styles.compactInfoBlock, { flex: 1 }]}>
                    <Text style={styles.labelSmallBold}>FAHRT:</Text>
                    <Text style={[styles.travelTimesShort, { fontStyle: 'italic' }]}>
                        {ttTo ? `Hinfahrt: ${ttTo}` : ''} {ttTo && ttReturn ? '· ' : ''} {ttReturn ? `Rückfahrt: ${ttReturn}` : ''}
                    </Text>
                </View>
                {workDur && (
                    <View style={styles.compactInfoBlock}>
                        <Text style={styles.labelSmallBold}>DAUER:</Text>
                        <Text style={[styles.travelTimesShort, { fontStyle: 'italic' }]}>{workDur}</Text>
                    </View>
                )}
            </View>
            
            {/* Ressourcen-Gruppierung */}
            <View style={{ marginTop: 2 }}>
                <Text style={styles.labelSmallBold}>TEAM:</Text>
                {Object.keys(groups).map(kid => (
                    <View key={kid} style={styles.kolonneContainerShort}>
                        <Text style={styles.kolonneHeadingShort}>Kolonne: {groups[kid].name}</Text>
                        
                        {groups[kid].workers.length > 0 && (
                            <View style={styles.resourceGroupRowShort}>
                                <Text style={styles.resourceSubLabelShort}>PERSONEN:</Text>
                                <Text style={[styles.assignmentResourcesShort, { marginTop: 0, flex: 1 }]}>
                                    {groups[kid].workers.map(w => w.worker_name).join(', ')}
                                </Text>
                            </View>
                        )}
                        
                        {groups[kid].vehicles.length > 0 && (
                            <View style={styles.resourceGroupRowShort}>
                                <Text style={styles.resourceSubLabelShort}>FAHRZEUGE:</Text>
                                <Text style={[styles.assignmentResourcesShort, { marginTop: 0, flex: 1 }]}>
                                    {groups[kid].vehicles.map(v => v.vehicle_plate || v.internal_name).filter(Boolean).join(', ')}
                                </Text>
                            </View>
                        )}
                    </View>
                ))}
                
                {(individuals.workers.length > 0 || individuals.vehicles.length > 0) && (
                    <View style={[styles.kolonneContainerShort, { borderLeftColor: 'transparent', paddingLeft: 0 }]}>
                        {Object.keys(groups).length > 0 && <Text style={styles.kolonneHeadingShort}>Einzelressourcen</Text>}
                        
                        {individuals.workers.length > 0 && (
                            <View style={styles.resourceGroupRowShort}>
                                <Text style={styles.resourceSubLabelShort}>PERSONEN:</Text>
                                <Text style={[styles.assignmentResourcesShort, { marginTop: 0, flex: 1 }]}>
                                    {individuals.workers.map(w => w.worker_name).join(', ')}
                                </Text>
                            </View>
                        )}
                        
                        {individuals.vehicles.length > 0 && (
                            <View style={styles.resourceGroupRowShort}>
                                <Text style={styles.resourceSubLabelShort}>FAHRZEUGE:</Text>
                                <Text style={[styles.assignmentResourcesShort, { marginTop: 0, flex: 1 }]}>
                                    {individuals.vehicles.map(v => v.vehicle_plate || v.internal_name).filter(Boolean).join(', ')}
                                </Text>
                            </View>
                        )}
                    </View>
                )}
            </View>

            {options.showNotes && assignment.notes && (
                <View style={[styles.assignmentNotesShort, { marginTop: 6 }]}>
                    <Text style={styles.labelSmallBold}>NOTIZ:</Text>
                    <Text style={{ fontStyle: 'italic' }}>{assignment.notes}</Text>
                </View>
            )}
        </View>
    );
};

/**
 * DETAILLIERTE VERSION: Großzügige, gestreckte Darstellung.
 * Nutzt viel Weißraum und eine klare Hierarchie für maximale Übersicht.
 */
export const PdfAssignmentCardLong = ({ assignment, options, companyLoc, kolonnen = [] }) => {
    const statusStyles = {
        aktiv: styles.badgeAktiv,
        pausiert: styles.badgePausiert,
        abgeschlossen: styles.badgeDone
    };
    const statusLabels = {
        aktiv: 'Aktiv',
        pausiert: 'Pausiert',
        abgeschlossen: 'Erledigt'
    };

    const { groups, individuals } = groupResources(assignment, kolonnen);

    // Hilfsfunktion für robuste Adress-Findung
    const getStartAddr = () => {
        const aAddr = assignment.start_address?.trim();
        if (aAddr) return aAddr;
        const cAddr = companyLoc?.address?.trim();
        if (cAddr) return cAddr;
        return null;
    };

    // Helfer für Dauer
    const formatDuration = (min) => min ? `${Math.floor(min / 60)}h ${String(min % 60).padStart(2, '0')}m` : '(keine Angabe)';
    const startAddr = getStartAddr();

    return (
        <View style={styles.assignmentCardLong} wrap={false}>
            {/* Header mit Titel und Status */}
            <View style={styles.assignmentHeader}>
                <Text style={styles.assignmentTitle}>{assignment.project_name}</Text>
                <Text style={[styles.assignmentBadge, statusStyles[assignment.status] || styles.badgeDone]}>
                    {statusLabels[assignment.status] || assignment.status}
                </Text>
            </View>

            {/* Metadaten-Bereich */}
            <View style={styles.assignmentSubHeader}>
                <View style={styles.assignmentMetaBlock}>
                    <Text style={styles.metaLabelSmall}>START & ZIEL</Text>
                    <Text style={styles.addressLabel}>STARTORT:</Text>
                    <Text style={styles.metaValueSmall}>{startAddr || '(Standard-Ort)'}</Text>
                    <Text style={styles.addressLabel}>EINSATZORT:</Text>
                    <Text style={styles.metaValueSmall}>{assignment.location || '(Einsatzort)'}</Text>
                </View>
                <View style={styles.assignmentMetaBlock}>
                    <Text style={styles.metaLabelSmall}>ZEITRAUM</Text>
                    <Text style={styles.metaValueSmall}>
                        {assignment.start_datetime ? (
                            <>
                                {formatDateRange(assignment.start_datetime, assignment.end_datetime)}
                                {"\n"}
                                {isAllDay(assignment) ? 'Ganztags-Einsatz' : `${formatTime(assignment.start_datetime)} - ${formatTime(assignment.end_datetime)} Uhr`}
                            </>
                        ) : (
                            "Undatiert (Kein Datum festgelegt)"
                        )}
                    </Text>
                </View>
            </View>

            {/* Fahrtzeiten-Bereich */}
            <View style={styles.travelTimesLong}>
                <View style={styles.assignmentSubHeader}>
                    <View style={styles.assignmentMetaBlock}>
                        <Text style={styles.metaLabelSmall}>HINFAHRT</Text>
                        <Text style={styles.metaValueSmall}>{formatDuration(assignment.travel_time_to)}</Text>
                    </View>
                    <View style={styles.assignmentMetaBlock}>
                        <Text style={styles.metaLabelSmall}>RÜCKFAHRT</Text>
                        <Text style={styles.metaValueSmall}>{formatDuration(assignment.travel_time_return)}</Text>
                    </View>
                    <View style={styles.assignmentMetaBlock}>
                        <Text style={styles.metaLabelSmall}>DAUER</Text>
                        <Text style={styles.metaValueSmall}>{formatDuration(assignment.work_duration)}</Text>
                    </View>
                </View>
            </View>

            {/* Ressourcen-Bereich mit Gruppierung */}
            <View style={styles.resourceSection}>
                <Text style={styles.metaLabelSmall}>PERSONAL & FAHRZEUGE</Text>
                
                {/* Kolonnen-Blöcke */}
                {Object.keys(groups).map(kid => (
                    <View key={kid} style={styles.kolonneSectionLong}>
                        <Text style={styles.kolonneTitleLong}>Kolonne: {groups[kid].name}</Text>
                        
                        {groups[kid].workers.length > 0 && (
                            <View>
                                <Text style={styles.resourceSubLabelLong}>PERSONEN:</Text>
                                <View style={styles.resourceRow}>
                                    {groups[kid].workers.map((w, wi) => (
                                        <Text key={wi} style={styles.resourceChip}>
                                            {w.worker_name}{w.assignment_role ? ` (${w.assignment_role})` : ''}
                                        </Text>
                                    ))}
                                </View>
                            </View>
                        )}
                        
                        {groups[kid].vehicles.length > 0 && (
                            <View>
                                <Text style={styles.resourceSubLabelLong}>FAHRZEUGE:</Text>
                                <View style={styles.resourceRow}>
                                    {groups[kid].vehicles.map((v, vi) => (
                                        <Text key={vi} style={styles.resourceChip}>
                                            {v.license_plate || v.internal_name || 'o. Kennz.'}
                                        </Text>
                                    ))}
                                </View>
                            </View>
                        )}
                    </View>
                ))}

                {/* Individual-Ressourcen */}
                {(individuals.workers.length > 0 || individuals.vehicles.length > 0) && (
                    <View style={{ marginTop: 8 }}>
                        {Object.keys(groups).length > 0 && <Text style={styles.kolonneTitleLong}>Einzelressourcen</Text>}
                        
                        {individuals.workers.length > 0 && (
                            <View>
                                <Text style={styles.resourceSubLabelLong}>PERSONEN:</Text>
                                <View style={styles.resourceRow}>
                                    {individuals.workers.map((w, wi) => (
                                        <Text key={wi} style={styles.resourceChip}>
                                            {w.worker_name}{w.assignment_role ? ` (${w.assignment_role})` : ''}
                                        </Text>
                                    ))}
                                </View>
                            </View>
                        )}
                        
                        {individuals.vehicles.length > 0 && (
                            <View>
                                <Text style={styles.resourceSubLabelLong}>FAHRZEUGE:</Text>
                                <View style={styles.resourceRow}>
                                    {individuals.vehicles.map((v, vi) => (
                                        <Text key={vi} style={styles.resourceChip}>
                                            {v.license_plate || v.internal_name || 'o. Kennz.'}
                                        </Text>
                                    ))}
                                </View>
                            </View>
                        )}
                    </View>
                )}
            </View>

            {/* Notizen-Bereich */}
            {options.showNotes && assignment.notes && (
                <View style={styles.notesBox}>
                    <Text style={styles.metaLabelSmall}>NOTIZEN</Text>
                    <Text style={{ marginTop: 4, fontStyle: 'italic' }}>{assignment.notes}</Text>
                </View>
            )}
        </View>
    );
};



/**
 * Unterschriftenbereich.
 */
export const PdfSignatures = () => (
    <View style={styles.signatureSection} wrap={false}>
        <View style={styles.signatureBlock}>
            <Text style={styles.signatureLabel}>Kolonnenführer</Text>
            <Text style={styles.signatureSub}>Name in Druckbuchstaben & Unterschrift</Text>
        </View>
        <View style={styles.signatureBlock}>
            <Text style={styles.signatureLabel}>Auftraggeber / Bauleiter</Text>
            <Text style={styles.signatureSub}>Name in Druckbuchstaben & Unterschrift</Text>
        </View>
    </View>
);
